import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Package,
  ClipboardList,
  BarChart3,
  Users,
  Globe,
  Palette,
  CreditCard,
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { tokenStorage, userApi } from '@/user/api';
import logoMini from '../../assest/imgs/kmzs-logo-mini.png';
import { ImagePreviewProvider } from './eCardManage/components/ImagePreview';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const topNavItems: NavItem[] = [
  { label: '企业资料', href: '/console/profile', icon: Building2 },
  { label: '商品管理', href: '/console/products', icon: Package },
  { label: '订单管理', href: '/console/orders', icon: ClipboardList },
];

const getNavGroups = (tenantLevel?: number): NavGroup[] => {
  const showAdvanced = tenantLevel === 5 || tenantLevel === 6;

  return [
    {
      title: '组织架构',
      items: [
        { label: '访客记录', href: '/console/ecardManage/index', icon: BarChart3 },
        // { label: '部门管理', href: '/console/ecardManage/department', icon: Users },
        { label: '员工管理', href: '/console/ecardManage/employee', icon: Users },
        // { label: '账号管理', href: '/console/ecardManage/account', icon: Users },
        // { label: '职位管理', href: '/console/ecardManage/positionManage', icon: Users },
      ]
    },
    {
      title: '企业配置',
      items: [
        // { label: '首页配置', href: '/console/ecardManage/homepage', icon: LayoutDashboard },
        ...(showAdvanced ? [{ label: '微官网配置', href: '/console/ecardManage/micrOfficialWebsite', icon: Globe }] : []),
        { label: '主题配置', href: '/console/ecardManage/theme', icon: Palette },
      ]
    },
    {
      title: '名片管理',
      items: [
        { label: '名片内容配置', href: '/console/ecardManage/electronicBusinessCards', icon: CreditCard },
        { label: '电子名片管理', href: '/console/ecardManage/UserEcardManage', icon: Users },
      ]
    },
  ];
};

export default function ConsoleLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(tokenStorage.getUserInfo());
  const [tenantInfo, setTenantInfo] = useState(tokenStorage.getTenantInfo());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const location = useLocation();
  const navigate = useNavigate();

  const navGroups = getNavGroups(tenantInfo?.tenantLevel);

  useEffect(() => {
    if (!tokenStorage.isLoggedIn()) {
      navigate('/login');
    }
    setUserInfo(tokenStorage.getUserInfo());
    setTenantInfo(tokenStorage.getTenantInfo());
  }, [navigate]);

  useEffect(() => {
    navGroups.forEach(group => {
      if (group.items.some(item => item.href === location.pathname)) {
        setExpandedGroups(prev => {
          if (prev.has(group.title)) return prev;
          return new Set(prev).add(group.title);
        });
      }
    });
  }, [location.pathname]);

  const toggleGroup = (title: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await userApi.logout();
      alert('退出登录成功');
      navigate('/');
    } catch {
      alert('退出登录失败');
    }
  };

  const isItemActive = (href: string) => location.pathname === href;

  const renderNavItem = (item: NavItem, isActive: boolean, onItemClick?: () => void) => {
    const Icon = item.icon;
    return (
      <Link
        key={item.href}
        to={item.href}
        onClick={onItemClick}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
          isActive
            ? 'bg-[#1c5bff] text-white shadow-md shadow-blue-500/25'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
      >
        <Icon className="w-5 h-5" />
        {item.label}
      </Link>
    );
  };

  const renderGroup = (group: NavGroup) => {
    const isExpanded = expandedGroups.has(group.title);
    const hasActive = group.items.some(item => isItemActive(item.href));
    return (
      <div key={group.title} className="mb-1">
        <button
          onClick={() => toggleGroup(group.title)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            hasActive ? 'text-[#1c5bff]' : 'text-gray-700'
          } hover:bg-gray-50`}
        >
          <span className="flex items-center gap-3">
            <span className="w-5 h-5 flex items-center justify-center">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
            {group.title}
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isExpanded ? 'max-h-96 opacity-100 mt-1 space-y-1 pl-4' : 'max-h-0 opacity-0'
          }`}
        >
          {group.items.map(item => renderNavItem(item, isItemActive(item.href)))}
        </div>
      </div>
    );
  };

  const renderGroupMobile = (group: NavGroup, onItemClick: () => void) => {
    const isExpanded = expandedGroups.has(group.title);
    return (
      <div key={group.title} className="mb-2">
        <button
          onClick={() => toggleGroup(group.title)}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <span className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
            {group.title}
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isExpanded ? 'max-h-96 opacity-100 mt-1 space-y-0.5 pl-4' : 'max-h-0 opacity-0'
          }`}
        >
          {group.items.map(item => renderNavItem(item, isItemActive(item.href), onItemClick))}
        </div>
      </div>
    );
  };

  return (
    <ImagePreviewProvider>
      <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200 fixed h-full">
        <div className="p-6 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 bg-[#1c5bff] rounded-lg flex items-center justify-center">
              <img src={logoMini} alt="孔明logo" className="h-full w-auto" />
            </div>
            <span className="font-bold text-gray-900">控制台</span>
          </Link>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1 mb-4">
            {topNavItems.map(item => renderNavItem(item, isItemActive(item.href)))}
          </div>
          {tenantInfo && tenantInfo.tenantType == 1 && tenantInfo.userId == userInfo?.id && (
            <div className="border-t border-gray-100 pt-4 space-y-1">
              {navGroups.map(group => renderGroup(group))}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {userInfo?.username || '用户'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-1"
          >
            <LogOut className="w-5 h-5" />
            退出登录
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 bg-[#1c5bff] rounded-lg flex items-center justify-center">
              <img src={logoMini} alt="孔明logo" className="h-full w-auto" />
            </div>
            <span className="font-bold text-gray-900">控制台</span>
          </Link>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="px-4 pb-4">
            <div className="space-y-0.5 mb-2">
              {topNavItems.map(item => renderNavItem(item, isItemActive(item.href), () => setIsMobileMenuOpen(false)))}
            </div>
            {tenantInfo && tenantInfo.tenantType == 1 && tenantInfo.userId == userInfo?.id && (
              <div className="border-t border-gray-100 pt-2">
                {navGroups.map(group => renderGroupMobile(group, () => setIsMobileMenuOpen(false)))}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors mt-2"
            >
              <LogOut className="w-5 h-5" />
              退出登录
            </button>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden lg:pl-64 pt-16 lg:pt-0">
        <div className="w-full max-w-full min-w-0 overflow-x-hidden p-4 lg:p-8 mx-auto" style={{ boxSizing: 'border-box' }}>
          <Outlet />
        </div>
      </main>
    </div>
    </ImagePreviewProvider>
  );
}
